import { calculateSlopeForTrendline } from 'libs/end-to-end/src/root-cause-analysis';
import { RootCauseAddingVariablesGraphGroupResult } from 'libs/end-to-end/src/root-cause-analysis';
import { RootCauseBinScatterControlledVsUncontrolledGroupResult } from 'libs/end-to-end/src/root-cause-analysis';
import { RootCauseGroupResult } from 'libs/end-to-end/src/root-cause-analysis';
import React, { useMemo } from 'react';
import { BinScatterChart } from 'apps/sigma2-ui/src/components/BinScatterChart';
import { DottedLineIcon } from 'apps/sigma2-ui/src/pages/FocusAreaPage/DottedLineIcon';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';

interface RootCauseBinScatterChartProps {
  download: React.ReactNode;
  dimension: string;
  focusArea: MyFocusAreaDefinition;
  groupResult:
    | RootCauseBinScatterControlledVsUncontrolledGroupResult
    | RootCauseAddingVariablesGraphGroupResult;
  metricDefinition: MyMetricDefinition;
  referenceGroupColor: string;
  treatmentGroupColor: string;
  referenceGroupDisplayName: string;
  treatmentGroupDisplayName: string;
  yAxisLabel: string;
  xAxisLabel: string | null;
}

const shouldTrendlinesDisplayOnGraph = (
  groupResult: RootCauseGroupResult,
): {
  referenceGroupShouldDisplay: boolean;
  treatmentGroupShouldDisplay: boolean;
} => {
  if (groupResult.plot_type === 'BinScatterControlledVsUncontrolled') {
    return {
      referenceGroupShouldDisplay:
        calculateSlopeForTrendline(groupResult.reference_level.trendline) !==
        'indeterminate',
      treatmentGroupShouldDisplay:
        calculateSlopeForTrendline(groupResult.treatment_level.trendline) !==
        'indeterminate',
    };
  } else {
    return {
      referenceGroupShouldDisplay: true,
      treatmentGroupShouldDisplay: true,
    };
  }
};

export const RootCauseBinScatterChart = ({
  download,
  dimension,
  focusArea,
  groupResult,
  referenceGroupColor,
  treatmentGroupColor,
  referenceGroupDisplayName,
  treatmentGroupDisplayName,
  yAxisLabel,
  xAxisLabel,
  metricDefinition,
}: RootCauseBinScatterChartProps) => {
  const { referenceGroupShouldDisplay, treatmentGroupShouldDisplay } =
    useMemo(() => {
      return shouldTrendlinesDisplayOnGraph(groupResult);
    }, [groupResult]);

  return (
    <Spacing direction="vertical" size="h-full">
      <Spacing direction="horizontal" size="justify-between" className="pr-5">
        <Spacing
          direction="horizontal"
          size="lg"
          className="w-full justify-center"
        >
          {referenceGroupShouldDisplay && (
            <div className="flex flex-row gap-2 items-center w-fit">
              <DottedLineIcon colorClass={referenceGroupColor} />
              <p className="font-sans text-sm">{referenceGroupDisplayName}</p>
            </div>
          )}

          {treatmentGroupShouldDisplay && (
            <div className="flex flex-row gap-2 items-center w-fit">
              <DottedLineIcon colorClass={treatmentGroupColor} />
              <p className="font-sans text-sm">{treatmentGroupDisplayName}</p>
            </div>
          )}
        </Spacing>

        {download}
      </Spacing>
      <div>
        <BinScatterChart
          dimension={dimension}
          groupResult={groupResult}
          focusArea={focusArea}
          metricDefinition={metricDefinition}
          overridingYAxisText={yAxisLabel}
          overridingXAxisText={xAxisLabel}
        />
      </div>
    </Spacing>
  );
};
